import {combineReducers} from 'redux';

import {METADATA_MODULE, metadataReducer} from 'entities/metadata';
import {customControlRatiosReducer, CUSTOM_CONTROL_RATIOS_SLICE} from 'features/calculate-control-ratios';
import {CUSTOM_LINK_SLICE, customLinkSliceReducer} from 'features/provide-custom-link';
import {CONTEXT_MODULE} from 'modules/context/context-constants';
import {contextReducer} from 'modules/context/context-reducer';
import {DATA_MODULE, dataReducer} from 'modules/data';
import {aisSliceReducer} from 'store/slices/ais-slice/ais-slice';
import {AIS3_SLICE} from 'store/slices/ais-slice/ais-slice-constants';
import {apiRequestProgressSliceReducer} from 'store/slices/api-request-progress-slice/api-request-progress-slice';
import {API_REQUEST_PROGRESS_SLICE} from 'store/slices/api-request-progress-slice/api-request-progress-slice-constants';
import {logout} from 'store/slices/auth-slice';
import {authSliceReducer} from 'store/slices/auth-slice/auth-slice';
import {AUTH_SLICE} from 'store/slices/auth-slice/auth-slice-constants';
import {BREADCRUMBS_SLICE, breadcrumbsSliceReducer} from 'store/slices/breadcrumbs-slice';
import {controlRatioReducer, CONTROL_RATIO_SLICE} from 'store/slices/control-ratio-slice';
import {descriptiveFlexfieldsReducer} from 'store/slices/descriptive-flexfields-slice/descriptive-flexfields-slice';
import {DESCRIPTIVE_FLEXFIELDS_SLICE} from 'store/slices/descriptive-flexfields-slice/descriptive-flexfields-slice-constants';
import {directoriesCompanyReducer} from 'store/slices/directories-company-slice/directories-company-slice';
import {DIRECTORIES_COMPANY_SLICE} from 'store/slices/directories-company-slice/directories-company-slice-constants';
import {DOCUMENTS_SLICE, documentsUploadingSliceReducer} from 'store/slices/documents-uploading-slice';
import {
    draggableFramesReducer,
    DRAGABBLE_FRAMES_SLICE,
} from 'store/slices/draggable-frames-slice';
import {ETL_SLICE, etlSliceReducer} from 'store/slices/etl-slice';
import {externalLoadingSliceReducer} from 'store/slices/external-loading/external-loading';
import {EXTERNAL_LOADING_SLICE} from 'store/slices/external-loading/external-loading-constants';
import {externalSystemsSliceReducer} from 'store/slices/external-systems-slice/external-systems-slice';
import {EXTERNAL_SYSTEMS_SLICE} from 'store/slices/external-systems-slice/external-systems-slice-constants';
import {fileUploadCellReducer} from 'store/slices/file-upload-cell-slice/file-upload-cell';
import {FILE_UPLOAD_CELL_SLICE} from 'store/slices/file-upload-cell-slice/file-upload-cell-constants';
import {flexFieldsSliceReducer} from 'store/slices/flex-fields-slice/flex-fields-slice';
import {FLEX_FIELDS_SLICE} from 'store/slices/flex-fields-slice/flex-fields-slice-constants';
import {jasperReportSliceReducer} from 'store/slices/jasper-report-slice/jasper-report-slice';
import {JASPER_REPORT_SLICE} from 'store/slices/jasper-report-slice/jasper-report-slice-constants';
import {
    LOADING_STATE_SLICE,
    loadingStateSliceReducer,
} from 'store/slices/loading-state-slice';
import {menuSliceReducer} from 'store/slices/menu-slice/menu-slice';
import {MENU_SLICE} from 'store/slices/menu-slice/menu-slice-constants';
import {modalsSliceReducer} from 'store/slices/modals-slice/modals-slice';
import {MODALS_SLICE} from 'store/slices/modals-slice/modals-slice-constants';
import {REGIONS_SLICE, regionsSliceReducer} from 'store/slices/regions-slice';
import {
    REPORT_CONFIGURATION_SLICE,
    reportConfigurationSliceReducer,
} from 'store/slices/report-configuration-slice';
import {tableReportSliceReducer} from 'store/slices/table-report-slice/table-report-slice';
import {TABLE_REPORT_SLICE} from 'store/slices/table-report-slice/table-report-slice-constants';
import {userSliceReducer} from 'store/slices/user-slice/user-slice';
import {USER_SLICE} from 'store/slices/user-slice/user-slice-constants';
import {WARNING_SLICE, warningSliceReducer} from 'store/slices/warning-slice';
import {widgetsSliceReducer} from 'store/slices/widgets-slice/widgets-slice';
import {WIDGETS_SLICE} from 'store/slices/widgets-slice/widgets-slice-constants';

import {apiReducers} from '../api';
import {
    documentUploadTemplateSliceReducer,
} from '../slices/document-upload-template/document-upload-template-slice';
import {
    DOCUMENT_UPLOAD_TEMPLATE_SLICE,
} from '../slices/document-upload-template/document-upload-template-slice-constants';
import {netDirectoryFileSliceReducer} from '../slices/net-directory-file-slice/net-directory-file-slice';
import {
    NET_DIRECTORY_FILE_SLICE,
} from '../slices/net-directory-file-slice/net-directory-file-slice-constants';
import {requestSliceReducer} from '../slices/request-slice/request-slice';
import {REQUEST_SLICE} from '../slices/request-slice/request-slice-constants';
import {TAction} from './types';

import {PAGE_INFO_MODULE} from '.deprecated/modules/page-info';
import {pageInfoReducer} from '.deprecated/modules/page-info/page-info.reducer';

const RESET_ACTION_TYPE = 'RESET';

const appReducer = combineReducers({
    ...apiReducers,
    [METADATA_MODULE]: metadataReducer,
    [DATA_MODULE]: dataReducer,
    [PAGE_INFO_MODULE]: pageInfoReducer, // deprecated
    [CONTEXT_MODULE]: contextReducer,
    // slices
    [DRAGABBLE_FRAMES_SLICE]: draggableFramesReducer,
    [CONTROL_RATIO_SLICE]: controlRatioReducer,
    [REPORT_CONFIGURATION_SLICE]: reportConfigurationSliceReducer,
    [LOADING_STATE_SLICE]: loadingStateSliceReducer,
    [DESCRIPTIVE_FLEXFIELDS_SLICE]: descriptiveFlexfieldsReducer,
    [WIDGETS_SLICE]: widgetsSliceReducer,
    [DOCUMENT_UPLOAD_TEMPLATE_SLICE]: documentUploadTemplateSliceReducer,
    [REQUEST_SLICE]: requestSliceReducer,
    [TABLE_REPORT_SLICE]: tableReportSliceReducer,
    [MODALS_SLICE]: modalsSliceReducer,
    [MENU_SLICE]: menuSliceReducer,
    [AIS3_SLICE]: aisSliceReducer,
    [EXTERNAL_LOADING_SLICE]: externalLoadingSliceReducer,
    [AUTH_SLICE]: authSliceReducer,
    [API_REQUEST_PROGRESS_SLICE]: apiRequestProgressSliceReducer,
    [USER_SLICE]: userSliceReducer,
    [NET_DIRECTORY_FILE_SLICE]: netDirectoryFileSliceReducer,
    [JASPER_REPORT_SLICE]: jasperReportSliceReducer,
    [FLEX_FIELDS_SLICE]: flexFieldsSliceReducer,
    [EXTERNAL_SYSTEMS_SLICE]: externalSystemsSliceReducer,
    [DIRECTORIES_COMPANY_SLICE]: directoriesCompanyReducer,
    [CUSTOM_LINK_SLICE]: customLinkSliceReducer,
    [DOCUMENTS_SLICE]: documentsUploadingSliceReducer,
    [FILE_UPLOAD_CELL_SLICE]: fileUploadCellReducer,
    [WARNING_SLICE]: warningSliceReducer,
    [CUSTOM_CONTROL_RATIOS_SLICE]: customControlRatiosReducer,
    [REGIONS_SLICE]: regionsSliceReducer,
    [BREADCRUMBS_SLICE]: breadcrumbsSliceReducer,
    [ETL_SLICE]: etlSliceReducer,
});

export const resetStore = () => ({type: RESET_ACTION_TYPE});

export const rootReducer = (state: ReturnType<typeof appReducer> | undefined, action: TAction<string>) => {
    if (action.type === `${logout.typePrefix}/fulfilled`) {
        return appReducer(undefined, action);
    }
    if (action.type === RESET_ACTION_TYPE) {
        return appReducer({
            ...appReducer(undefined, action),
            [AUTH_SLICE]: state ? state[AUTH_SLICE] : appReducer(undefined, action)[AUTH_SLICE],
        }, action);
    }
    return appReducer(state, action);
};
